import { useCallback, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import BranchIcon from 'assets/settings/components/BranchIcon';
import DepartmentIcon from 'assets/settings/components/DepartmentIcon';
import TeamIcon from 'assets/settings/components/TeamIcon';
import UserIcon from 'assets/settings/components/UserIcon';
import Modal from 'common-ui/modal';
import { Select } from 'common-ui/select';
import {
  getBranchesAll,
  getDepartmentsAll,
  getPrivilegeAssignmentsByPrivilege,
  getTeamsAll,
} from 'features/newsales/newsalesActions';
import {
  selectPrivilegeAssignments,
  selectPrivilegeAssignmentsLoading,
} from 'features/newsales/newsalesSlice';
import { getUsers } from 'features/users/usersActions';
import { notifyError } from 'utils/notifyConfig';

import {
  AssignmentSummarySection,
  EmptyState,
  HintText,
  ModalFormContent,
  SummaryContent,
  SummaryLabel,
  SummaryRow,
  SummaryTag,
  SummaryTags,
  SummaryTitle,
  Tab,
  TabsContainer,
} from './AssignPrivilegesModal.styles';

const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
    <path
      fillRule="evenodd"
      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
      clipRule="evenodd"
    />
  </svg>
);

const TABS = [
  { id: 'user', label: 'User', icon: UserIcon },
  { id: 'team', label: 'Team', icon: TeamIcon },
  { id: 'branch', label: 'Branch', icon: BranchIcon },
  { id: 'department', label: 'Department', icon: DepartmentIcon },
];

const AssignPrivilegesModal = ({ isOpen, onClose, onAssign, privileges = [] }) => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState('user');
  const [selectedPrivilege, setSelectedPrivilege] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [mergedItems, setMergedItems] = useState([]);
  const [existingAssignmentItems, setExistingAssignmentItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [teams, setTeams] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [teamSearchTerm, setTeamSearchTerm] = useState('');
  const [branches, setBranches] = useState([]);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [branchSearchTerm, setBranchSearchTerm] = useState('');
  const [departments, setDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [departmentSearchTerm, setDepartmentSearchTerm] = useState('');
  const existingAssignments = useSelector(selectPrivilegeAssignments);
  const loadingAssignments = useSelector(selectPrivilegeAssignmentsLoading);

  const fetchUsers = useCallback(
    async (searchTerm = '') => {
      if (activeTab !== 'user') return;

      setLoadingUsers(true);
      try {
        const result = await dispatch(
          getUsers({
            disabled: false,
            limit: 10000,
            offset: 0,
            search: searchTerm,
          })
        );

        if (result.payload && result.payload.users) {
          setUsers(result.payload.users);
        }
      } catch (error) {
        console.error('Error fetching users:', error);
        notifyError('Failed to fetch users');
      } finally {
        setLoadingUsers(false);
      }
    },
    [dispatch, activeTab]
  );

  const fetchTeams = useCallback(
    async (searchTerm = '') => {
      if (activeTab !== 'team') return;

      setLoadingTeams(true);
      try {
        const result = await dispatch(
          getTeamsAll({
            limit: 10000,
            offset: 0,
            search: searchTerm,
          })
        );

        if (result.payload && result.payload.teams) {
          setTeams(result.payload.teams);
        }
      } catch (error) {
        console.error('Error fetching teams:', error);
        notifyError('Failed to fetch teams');
      } finally {
        setLoadingTeams(false);
      }
    },
    [dispatch, activeTab]
  );

  const fetchBranches = useCallback(
    async (searchTerm = '') => {
      if (activeTab !== 'branch') return;

      setLoadingBranches(true);
      try {
        const result = await dispatch(
          getBranchesAll({
            limit: 10000,
            offset: 0,
            search: searchTerm,
          })
        );

        if (result.payload && result.payload.branches) {
          setBranches(result.payload.branches);
        }
      } catch (error) {
        console.error('Error fetching branches:', error);
        notifyError('Failed to fetch branches');
      } finally {
        setLoadingBranches(false);
      }
    },
    [dispatch, activeTab]
  );

  const fetchDepartments = useCallback(
    async (searchTerm = '') => {
      if (activeTab !== 'department') return;

      setLoadingDepartments(true);
      try {
        const result = await dispatch(
          getDepartmentsAll({
            limit: 10000,
            offset: 0,
            search: searchTerm,
          })
        );

        if (result.payload && result.payload.departments) {
          setDepartments(result.payload.departments);
        }
      } catch (error) {
        console.error('Error fetching departments:', error);
        notifyError('Failed to fetch departments');
      } finally {
        setLoadingDepartments(false);
      }
    },
    [dispatch, activeTab]
  );

  useEffect(() => {
    if (isOpen && activeTab === 'user') {
      const debounceTimeout = setTimeout(() => {
        fetchUsers(userSearchTerm);
      }, 300);

      return () => clearTimeout(debounceTimeout);
    }
  }, [isOpen, activeTab, fetchUsers, userSearchTerm]);

  useEffect(() => {
    if (isOpen && activeTab === 'team') {
      const debounceTimeout = setTimeout(() => {
        fetchTeams(teamSearchTerm);
      }, 300);

      return () => clearTimeout(debounceTimeout);
    }
  }, [isOpen, activeTab, fetchTeams, teamSearchTerm]);

  useEffect(() => {
    if (isOpen && activeTab === 'branch') {
      const debounceTimeout = setTimeout(() => {
        fetchBranches(branchSearchTerm);
      }, 300);

      return () => clearTimeout(debounceTimeout);
    }
  }, [isOpen, activeTab, fetchBranches, branchSearchTerm]);

  useEffect(() => {
    if (isOpen && activeTab === 'department') {
      const debounceTimeout = setTimeout(() => {
        fetchDepartments(departmentSearchTerm);
      }, 300);

      return () => clearTimeout(debounceTimeout);
    }
  }, [isOpen, activeTab, fetchDepartments, departmentSearchTerm]);

  useEffect(() => {
    if (isOpen && selectedPrivilege) {
      setSelectedItems([]);
      setMergedItems([]);
      dispatch(
        getPrivilegeAssignmentsByPrivilege({
          privilegeId: selectedPrivilege.value,
          level: activeTab,
        })
      );
    }
  }, [isOpen, selectedPrivilege, activeTab, dispatch]);

  useEffect(() => {
    const mapped = existingAssignments.map((a) => ({
      value: a.uuid,
      label: a.name,
      email: a.email,
    }));
    setMergedItems(mapped);
    setExistingAssignmentItems(mapped);
  }, [existingAssignments]);

  useEffect(() => {
    if (!isOpen) {
      setActiveTab('user');
      setSelectedPrivilege(null);
      setSelectedItems([]);
      setMergedItems([]);
      setExistingAssignmentItems([]);
      setUsers([]);
      setUserSearchTerm('');
      setTeams([]);
      setTeamSearchTerm('');
      setBranches([]);
      setBranchSearchTerm('');
      setDepartments([]);
      setDepartmentSearchTerm('');
    }
  }, [isOpen]);

  const handleClose = () => {
    setActiveTab('user');
    setSelectedPrivilege(null);
    setSelectedItems([]);
    setMergedItems([]);
    setExistingAssignmentItems([]);
    setUsers([]);
    setUserSearchTerm('');
    setTeams([]);
    setTeamSearchTerm('');
    setBranches([]);
    setBranchSearchTerm('');
    setDepartments([]);
    setDepartmentSearchTerm('');
    onClose();
  };

  const handleSubmit = () => {
    if (!selectedPrivilege) {
      notifyError('Please select a privilege');
      return;
    }

    if (mergedItems.length === 0) {
      notifyError('Please assign to at least one item');
      return;
    }

    const dataToSend = {
      privilegeId: selectedPrivilege.value,
      assignmentType: activeTab,
      itemIds: mergedItems.map((item) => item.value),
      selectedItems: mergedItems,
    };

    onAssign(dataToSend);
    handleClose();
  };

  const handleSelectChange = (options) => {
    const newOptions = options || [];
    const removedValues = new Set(
      selectedItems
        .filter((item) => !newOptions.find((o) => o.value === item.value))
        .map((i) => i.value)
    );
    setSelectedItems(newOptions);
    setMergedItems((prev) => {
      const prevValues = new Set(prev.map((i) => i.value));
      const toAdd = newOptions.filter((o) => !prevValues.has(o.value));
      return [...prev.filter((i) => !removedValues.has(i.value)), ...toAdd];
    });
  };

  const handleRemoveItem = (itemToRemove) => {
    setMergedItems((prev) => prev.filter((item) => item.value !== itemToRemove.value));
    setSelectedItems((prev) => prev.filter((item) => item.value !== itemToRemove.value));
    setExistingAssignmentItems((prev) =>
      prev.filter((item) => item.value !== itemToRemove.value)
    );
  };

  const getOptionsForActiveTab = () => {
    switch (activeTab) {
      case 'user':
        return users.map((user) => {
          const fullName =
            user.name && user.surname
              ? `${user.name} ${user.surname}`.trim()
              : user.name || user.surname || user.email;

          return {
            value: user.uuid,
            label: fullName,
            email: user.email,
            type: user.userType,
            photo: user.photo,
            phoneNumber: user.phoneNumber,
            occupation: user.occupation,
            data: user,
          };
        });
      case 'team':
        return teams.map((team) => {
          const leadName = team.lead
            ? `${team.lead.name} ${team.lead.surname}`.trim()
            : 'No lead';

          return {
            value: team.uuid,
            label: team.name,
            description: team.description,
            lead: leadName,
            membersCount: team.members?.length || 0,
            data: team,
          };
        });
      case 'branch':
        return branches.map((branch) => {
          const headName = branch.head
            ? `${branch.head.name} ${branch.head.surname}`.trim()
            : 'No head';

          return {
            value: branch.uuid,
            label: branch.name,
            description: branch.description,
            head: headName,
            teamsCount: branch.teams || 0,
            regions: branch.regions || [],
            data: branch,
          };
        });
      case 'department':
        return departments.map((dept) => {
          const headName = dept.head
            ? `${dept.head.name} ${dept.head.surname}`.trim()
            : 'No head';

          return {
            value: dept.uuid,
            label: dept.name,
            description: dept.description,
            head: headName,
            branchesCount: dept.branches || 0,
            data: dept,
          };
        });
      default:
        return [];
    }
  };

  const getAssignmentLabel = () => {
    switch (activeTab) {
      case 'user':
        return 'Assign to Users';
      case 'team':
        return 'Assign to Teams';
      case 'branch':
        return 'Assign to Branches';
      case 'department':
        return 'Assign to Departments';
      default:
        return 'Assign to';
    }
  };

  const privilegeOptions = privileges.map((privilege) => ({
    value: privilege.id,
    label: privilege.name,
  }));

  const mergedValues = new Set(mergedItems.map((i) => i.value));
  const assignOptions = getOptionsForActiveTab().filter((o) => !mergedValues.has(o.value));

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      onOk={handleSubmit}
      title="Assign Privileges"
      footer
      closeIcon
      okText="Assign"
      cancelText="Cancel"
      width="500px"
    >
      <div>
        <TabsContainer>
          {TABS.map((tab) => (
            <Tab
              key={tab.id}
              $active={activeTab === tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                setSelectedPrivilege(null);
                setSelectedItems([]);
                setMergedItems([]);
                setExistingAssignmentItems([]);
              }}
            >
              <tab.icon />
              {tab.label}
            </Tab>
          ))}
        </TabsContainer>

        <ModalFormContent>
          <Select
            label="Select Privilege"
            value={selectedPrivilege}
            onChange={(option) => setSelectedPrivilege(option)}
            options={privilegeOptions}
            placeholder="Choose a privilege"
            isClearable
            req
          />

          <div>
            <Select
              label={getAssignmentLabel()}
              value={selectedItems}
              onChange={handleSelectChange}
              onInputChange={(value) => {
                if (activeTab === 'user') {
                  setUserSearchTerm(value);
                } else if (activeTab === 'team') {
                  setTeamSearchTerm(value);
                } else if (activeTab === 'branch') {
                  setBranchSearchTerm(value);
                } else if (activeTab === 'department') {
                  setDepartmentSearchTerm(value);
                }
              }}
              options={assignOptions}
              placeholder={`Type to search ${activeTab}s...`}
              isMulti
              isClearable
              isDisabled={!selectedPrivilege}
              isLoading={
                (activeTab === 'user' && loadingUsers) ||
                (activeTab === 'team' && loadingTeams) ||
                (activeTab === 'branch' && loadingBranches) ||
                (activeTab === 'department' && loadingDepartments)
              }
            />
            <HintText>Multiple {activeTab}s can be assigned to the same privilege</HintText>
          </div>

          <AssignmentSummarySection>
            <SummaryTitle>Assignment Summary</SummaryTitle>
            <SummaryContent>
              <SummaryRow>
                <SummaryLabel>
                  {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}s
                </SummaryLabel>
                {loadingAssignments ? (
                  <EmptyState>Loading...</EmptyState>
                ) : existingAssignmentItems.length > 0 ? (
                  <SummaryTags>
                    {existingAssignmentItems.map((item) => (
                      <SummaryTag key={item.value}>
                        {item.label}
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(item)}
                          aria-label="Remove"
                        >
                          <CloseIcon />
                        </button>
                      </SummaryTag>
                    ))}
                  </SummaryTags>
                ) : (
                  <EmptyState>No {activeTab}s assigned</EmptyState>
                )}
              </SummaryRow>
            </SummaryContent>
          </AssignmentSummarySection>
        </ModalFormContent>
      </div>
    </Modal>
  );
};

export default AssignPrivilegesModal;
